/**
 * SIDE PANEL: PROSPECT DETAIL
 * Panel latéral pour consulter et modifier un prospect (soumission de formulaire)
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { ExternalLink, User, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import {
  Form,
  FormInput,
  FormPhoneInput,
  FormSelect,
  FormTextarea,
  Checkbox,
  Label,
  toast,
  Button,
  Badge,
} from '../ui';
import { SidePanel, SidePanelFooter } from '../ui/side-panel';
import { PaymentSection } from '../payment/PaymentSection';
import type { FormSubmission } from '../../types/form';
import { useFormFields } from '../../hooks/useFormFields';
import { useUsers } from '../../contexts/UsersContext';
import { usePermissions } from '../../hooks/usePermissions';
import { CreateTaskModal, AssignUserModal } from '../modals';
import { COUNTRIES } from '../../types/school';

/**
 * Obtient le label lisible pour une option de select
 * Gère spécifiquement les codes pays (FRA -> France)
 */
function getOptionLabel(option: string, contactFieldMap?: string): string {
  if (contactFieldMap === 'country') {
    const country = COUNTRIES.find((c) => c.code === option);
    return country?.label || option;
  }
  return option;
}

// Schema simplifié - data contient tous les champs du formulaire
const updateProspectSchema = z.object({
  // Données du formulaire (contient first_name, last_name, email, phone, etc.)
  data: z.record(z.string(), z.any()),

  // Champs spéciaux du contact
  notes: z.string().optional(),
});

type UpdateProspectInput = z.infer<typeof updateProspectSchema>;

interface ProspectDetailPanelProps {
  isOpen: boolean;
  onClose: () => void;
  prospect: FormSubmission | null;
  teammate?: FormSubmission | null;
  onUpdate: (
    prospectId: string,
    contactId: string,
    updates: {
      contact: Partial<any>;
      submission: { data: Record<string, any> };
    }
  ) => Promise<void>;
  onDelete?: (prospectId: string) => Promise<void>;
}

export function ProspectDetailPanel({
  isOpen,
  onClose,
  prospect,
  teammate,
  onUpdate,
  onDelete,
}: ProspectDetailPanelProps) {
  const navigate = useNavigate();
  const { users } = useUsers();
  const { canDeleteEntity, canManageAssignments } = usePermissions();
  const [isCreateTaskModalOpen, setIsCreateTaskModalOpen] = useState(false);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [currentAssignedTo, setCurrentAssignedTo] = useState<string | null>(null);

  // Charger les champs du formulaire
  const { fields } = useFormFields({
    formId: prospect?.form_id,
    autoLoad: isOpen && !!prospect,
  });

  // Filtrer les champs pour n'afficher que ceux présents dans les données du prospect
  // Cela permet d'afficher uniquement les champs pilote pour un prospect pilote
  // et uniquement les champs copilote pour un prospect copilote
  const visibleFields = useMemo(() => {
    if (!prospect?.data || !fields.length) return fields;

    const dataKeys = Object.keys(prospect.data);

    return fields.filter((field) => {
      // Si le champ n'a pas de target_role (champ commun), toujours l'afficher
      if (!field.target_role) return true;

      // Si le champ a une valeur dans data, l'afficher
      return dataKeys.includes(field.id);
    });
  }, [fields, prospect?.data]);

  const form = useForm<UpdateProspectInput>({
    resolver: zodResolver(updateProspectSchema),
  });

  // Réinitialiser le formulaire quand le prospect change
  useEffect(() => {
    if (prospect) {
      form.reset({
        data: prospect.data || {},
        notes: prospect.contact?.notes || '',
      });
      setCurrentAssignedTo(prospect.contact?.assigned_to || null);
    }
  }, [prospect, form]);

  if (!prospect) return null;

  // Trouver l'utilisateur assigné
  const assignedUser = users.find((u) => u.id === currentAssignedTo);

  /**
   * Trouver l'ID du champ qui mappe à un champ contact
   */
  const getFieldIdByMapping = (contactField: string): string => {
    const field = fields.find((f) => f.contact_field_map === contactField);
    return field?.id || '';
  };

  const handleFormSubmit = async (formData: UpdateProspectInput) => {
    if (!prospect.contact) {
      toast.error('Aucun contact associé');
      return;
    }

    try {
      // Extraire les champs contact depuis data (via le mapping)
      const firstNameFieldId = getFieldIdByMapping('first_name');
      const lastNameFieldId = getFieldIdByMapping('last_name');
      const emailFieldId = getFieldIdByMapping('email');
      const phoneFieldId = getFieldIdByMapping('phone');
      const socioProfFieldId = getFieldIdByMapping('socio_professional_category');
      const isNotStudentFieldId = getFieldIdByMapping('is_not_student');

      // Calculer is_not_student et socio_professional_category
      let isNotStudent = prospect.contact.is_not_student || false;
      let socioProfCategory = prospect.contact.socio_professional_category || null;

      // Si le champ "Statut" (Étudiant/Jeune Actif) est présent
      if (isNotStudentFieldId) {
        const statusValue = formData.data[isNotStudentFieldId];
        if (statusValue) {
          isNotStudent = statusValue === 'Jeune Actif';
        }
      }

      // Si le champ "Profession" est présent
      if (socioProfFieldId) {
        const professionValue = formData.data[socioProfFieldId];
        if (professionValue && professionValue.trim() !== '') {
          socioProfCategory = professionValue;
          // Si une profession est renseignée, considérer automatiquement comme "plus étudiant"
          isNotStudent = true;
        }
      }

      const contactUpdate = {
        first_name: firstNameFieldId
          ? formData.data[firstNameFieldId]
          : prospect.contact.first_name,
        last_name: lastNameFieldId ? formData.data[lastNameFieldId] : prospect.contact.last_name,
        email: emailFieldId ? formData.data[emailFieldId] : prospect.contact.email,
        phone: phoneFieldId ? formData.data[phoneFieldId] : prospect.contact.phone,
        notes: formData.notes,
        // Nouveaux champs pour la catégorie socio-professionnelle
        is_not_student: isNotStudent,
        socio_professional_category: socioProfCategory,
      };

      await onUpdate(prospect.id, prospect.contact.id, {
        contact: contactUpdate,
        submission: {
          data: formData.data,
        },
      });
    } catch (error) {
      console.error('Error updating prospect:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const handleDelete = async () => {
    if (onDelete) {
      await onDelete(prospect.id);
    }
  };

  // Gérer l'assignation d'utilisateur (sauvegarde directe)
  async function handleAssignUser(userId: string) {
    if (!prospect || !prospect.contact) {
      toast.error('Aucun contact associé');
      return;
    }

    try {
      await onUpdate(prospect.id, prospect.contact.id, {
        contact: { assigned_to: userId },
        submission: { data: prospect.data || {} },
      });
      setCurrentAssignedTo(userId);
      toast.success('Utilisateur assigné');
    } catch (error) {
      console.error('Error assigning user:', error);
      toast.error("Erreur lors de l'assignation");
    }
  }

  // Gérer le retrait d'assignation
  async function handleUnassignUser() {
    if (!prospect || !prospect.contact) {
      toast.error('Aucun contact associé');
      return;
    }

    try {
      await onUpdate(prospect.id, prospect.contact.id, {
        contact: { assigned_to: null },
        submission: { data: prospect.data || {} },
      });
      setCurrentAssignedTo(null);
      toast.success('Assignation retirée');
    } catch (error) {
      console.error('Error unassigning user:', error);
      toast.error("Erreur lors du retrait de l'assignation");
    }
  }

  return (
    <SidePanel
      isOpen={isOpen}
      onClose={handleClose}
      title={`Prospect - ${prospect.contact?.email || 'Sans email'}`}
      width="xl"
      onDelete={canDeleteEntity('prospect') && onDelete ? handleDelete : undefined}
      footer={
        <SidePanelFooter
          onCancel={handleClose}
          submitLabel="Enregistrer"
          submitDisabled={!form.formState.isDirty}
          isSubmitting={form.formState.isSubmitting}
          formId="prospect-form"
        />
      }
    >
      <div className="flex flex-col gap-6">
        {/* Section 1 : Informations (READ-ONLY) */}
        <div className="flex flex-col gap-4">
          <h3 className="text-sm font-semibold text-zinc-900">Informations</h3>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Origine</Label>
              <div className="mt-1">
                {prospect.form?.origin?.label ? (
                  <Badge variant="secondary">{prospect.form.origin.label}</Badge>
                ) : (
                  <span className="text-sm text-zinc-500">-</span>
                )}
              </div>
            </div>

            <div>
              <Label>Produit</Label>
              <div className="mt-1">
                {prospect.form?.product?.name ? (
                  <span className="text-sm">{prospect.form.product.name}</span>
                ) : (
                  <span className="text-sm text-zinc-500">-</span>
                )}
              </div>
            </div>

            <div>
              <Label>Date de soumission</Label>
              <div className="mt-1">
                <span className="text-sm">
                  {new Date(prospect.created_at).toLocaleString('fr-FR')}
                </span>
              </div>
            </div>

            <div>
              <Label>Formulaire</Label>
              <div className="mt-1">
                <span className="text-sm">{prospect.form?.name || '-'}</span>
              </div>
            </div>

            {prospect.bpi_code && (
              <div>
                <Label>Numéro BPI</Label>
                <div className="mt-1">
                  <Badge variant="outline" className="text-primary border-primary font-mono">
                    {prospect.bpi_code}
                  </Badge>
                </div>
              </div>
            )}

            {teammate && (
              <div className="col-span-2">
                <Label>Coéquipier (même BPI)</Label>
                <div className="mt-1 flex items-center gap-2">
                  <User className="size-4 text-zinc-500" />
                  <span className="text-sm font-medium text-zinc-900">
                    {(teammate as any).contact_first_name} {(teammate as any).contact_last_name}
                  </span>
                  {(teammate as any).contact_email && (
                    <span className="text-sm text-zinc-500">
                      ({(teammate as any).contact_email})
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Section 2 : Lien vers le contact */}
        {prospect.contact && (
          <div className="flex flex-col gap-4">
            <h3 className="text-sm font-semibold text-zinc-900">Contact associé</h3>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(`/contacts/${prospect.contact!.id}`)}
                className="w-fit"
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                Voir la fiche contact
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsCreateTaskModalOpen(true)}
                className="w-fit"
              >
                Créer une tâche
              </Button>
            </div>

            {/* Assignation d'utilisateur - uniquement pour admin/superadmin */}
            {canManageAssignments() && (
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-2">Assigné à</label>
                <button
                  type="button"
                  onClick={() => setIsUserModalOpen(true)}
                  className="w-full flex items-center justify-between px-3 py-2 text-sm border border-zinc-300 rounded-md bg-white hover:bg-zinc-50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <User className="size-4 text-zinc-400" />
                    <span className={assignedUser ? 'text-zinc-900' : 'text-zinc-500'}>
                      {assignedUser ? assignedUser.full_name || assignedUser.email : 'Non assigné'}
                    </span>
                  </div>
                  <ChevronDown className="size-4 text-zinc-400" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Section Paiement BPI (uniquement si contact associé) */}
        {prospect.contact && (
          <PaymentSection
            contactId={prospect.contact.id}
            contactName={`${prospect.contact.first_name || ''} ${prospect.contact.last_name || ''}`}
            compact
          />
        )}

        {/* Section 3 : Réponses (EDITABLE) - Tous les champs en une seule section */}
        <Form {...form}>
          <form
            id="prospect-form"
            onSubmit={form.handleSubmit(handleFormSubmit)}
            className="flex flex-col gap-4"
          >
            <h3 className="text-sm font-semibold text-zinc-900">Réponses</h3>

            {visibleFields.length === 0 ? (
              <p className="text-sm text-zinc-500">Chargement des champs...</p>
            ) : (
              <div className="flex flex-col gap-4">
                {/* Champs du prospect (filtrés par rôle pour les formulaires duo) */}
                {visibleFields.map((field) => {
                  const fieldName = `data.${field.id}` as any;

                  // Text input
                  if (field.field_type === 'text') {
                    return (
                      <FormInput
                        key={field.id}
                        control={form.control}
                        name={fieldName}
                        label={field.label}
                        placeholder={field.placeholder || ''}
                        required={field.is_required}
                      />
                    );
                  }

                  // Email input
                  if (field.field_type === 'email') {
                    return (
                      <FormInput
                        key={field.id}
                        control={form.control}
                        name={fieldName}
                        label={field.label}
                        type="email"
                        placeholder={field.placeholder || 'email@example.com'}
                        required={field.is_required}
                      />
                    );
                  }

                  // Phone input
                  if (field.field_type === 'phone') {
                    return (
                      <FormPhoneInput
                        key={field.id}
                        control={form.control}
                        name={fieldName}
                        label={field.label}
                        placeholder={field.placeholder || '6 12 34 56 78'}
                        required={field.is_required}
                      />
                    );
                  }

                  // Number input
                  if (field.field_type === 'number') {
                    return (
                      <FormInput
                        key={field.id}
                        control={form.control}
                        name={fieldName}
                        label={field.label}
                        type="number"
                        placeholder={field.placeholder || ''}
                        required={field.is_required}
                        valueAsNumber
                      />
                    );
                  }

                  // Date input
                  if (field.field_type === 'date') {
                    return (
                      <FormInput
                        key={field.id}
                        control={form.control}
                        name={fieldName}
                        label={field.label}
                        type="date"
                        required={field.is_required}
                      />
                    );
                  }

                  // Checkbox - Simplifié pour éviter le bug d'affichage
                  if (field.field_type === 'checkbox') {
                    return (
                      <div key={field.id} className="flex flex-col gap-1">
                        <Checkbox
                          checked={form.watch(fieldName) || false}
                          onChange={(checked) =>
                            form.setValue(fieldName, checked, { shouldDirty: true })
                          }
                          label={field.label}
                        />
                      </div>
                    );
                  }

                  // Textarea
                  if (field.field_type === 'textarea') {
                    return (
                      <FormTextarea
                        key={field.id}
                        control={form.control}
                        name={fieldName}
                        label={field.label}
                        placeholder={field.placeholder || ''}
                        rows={3}
                        required={field.is_required}
                      />
                    );
                  }

                  // Select
                  if (field.field_type === 'select' && field.options) {
                    const selectOptions = field.options.map((opt) => ({
                      value: opt,
                      label: getOptionLabel(opt, field.contact_field_map),
                    }));
                    return (
                      <FormSelect
                        key={field.id}
                        control={form.control}
                        name={fieldName}
                        label={field.label}
                        options={selectOptions}
                        required={field.is_required}
                      />
                    );
                  }

                  return null;
                })}

                {/* Notes */}
                <div className="pt-4 border-t border-zinc-200">
                  <FormTextarea
                    control={form.control}
                    name="notes"
                    label="Notes"
                    placeholder="Notes sur ce prospect..."
                    rows={4}
                  />
                </div>
              </div>
            )}
          </form>
        </Form>

        {/* Pas de contact associé */}
        {!prospect.contact && (
          <div className="flex flex-col gap-4">
            <h3 className="text-sm font-semibold text-zinc-900">Contact associé</h3>
            <div className="p-4 bg-amber-50 rounded-md border border-amber-200">
              <p className="text-sm text-amber-800">
                Aucun contact associé à ce prospect. Le contact sera créé automatiquement si un
                email est fourni.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Modale Créer une tâche */}
      {prospect && (
        <CreateTaskModal
          isOpen={isCreateTaskModalOpen}
          onClose={() => setIsCreateTaskModalOpen(false)}
          entityType={prospect.contact_id ? 'contact' : 'prospect'}
          entityId={prospect.contact_id || prospect.id}
          onSuccess={() => {
            // Si le prospect a un contact lié, la tâche sera visible dans l'historique du contact
            // Sinon, elle reste liée au prospect (mais ne sera pas visible dans la timeline)
          }}
        />
      )}

      {/* Modale d'assignation d'utilisateur - uniquement pour admin/superadmin */}
      {canManageAssignments() && (
        <AssignUserModal
          isOpen={isUserModalOpen}
          onClose={() => setIsUserModalOpen(false)}
          currentUserId={currentAssignedTo}
          onAssign={handleAssignUser}
          onUnassign={handleUnassignUser}
          showUnassignOption={true}
          title="Assigner le prospect à"
        />
      )}
    </SidePanel>
  );
}
